import React from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';

const History = () => {
  const headerImage = "https://grafiken.trust-franken.de/HP/94977.jpg";
  const logoUrl = "https://grafiken.trust-franken.de/HP/Kreuz.png";
  
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const historyItems = [
    {
      year: '2019',
      title: 'Iron Fist MC Franken',
      image: 'https://trustmc.eu/assets/images/iffranken-591x591.png',
      desc: 'Die Geburtsstunde unserer Gemeinschaft in der Region Franken. Ein Fundament aus Respekt und Vision.'
    },
    {
      year: '2020',
      title: 'Trust MC Franken',
      image: 'https://trustmc.eu/assets/images/franken-591x591.png',
      desc: 'Ein neuer Name, das gleiche Ziel: Unabhängigkeit und Zusammenhalt. Der Weg in eine neue Ära.'
    },
    {
      year: '2024',
      title: 'Iron Fist MC Central',
      image: 'https://trustmc.eu/assets/images/ifcentral-1-591x591.png',
      desc: 'Gründung des Iron Fist MC Central Chapters'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-white z-[60] origin-left"
        style={{ scaleX }}
      />

      {/* Hero Header Section */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-end overflow-hidden">
        <motion.img 
          initial={{ y: 0 }}
          animate={{ y: 50 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
          src={headerImage} 
          alt="History Header" 
          className="absolute inset-0 w-full h-[120%] object-cover opacity-60" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-16">
          <div className="flex flex-col-reverse md:flex-row items-center md:items-end justify-between gap-10">
            <motion.div 
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="text-center md:text-left"
            >
              <h1 className="text-6xl md:text-9xl font-black uppercase tracking-tighter leading-none">
                History
              </h1>
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 1, duration: 1 }}
                className="h-2 bg-white mt-4"
              />
            </motion.div>
            
            <motion.img 
              initial={{ opacity: 0, scale: 0, rotate: -45 }}
              animate={{ opacity: 1, scale: 1, rotate: 0, y: [0, -20, 0] }}
              transition={{ duration: 1.2, y: { duration: 4, repeat: Infinity, ease: "easeInOut" } }}
              whileHover={{ scale: 1.1, filter: 'brightness(1.5)' }}
              src={logoUrl} 
              alt="Trust MC Logo" 
              className="h-40 md:h-64 w-auto drop-shadow-[0_0_50px_rgba(255,255,255,0.2)]" 
            />
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-40 px-6 relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10 hidden lg:block" />
        
        <div className="max-w-6xl mx-auto">
          <div className="space-y-48">
            {historyItems.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: "circOut" }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-16`}
              >
                <div className="w-full lg:w-1/2">
                  <motion.div 
                    whileHover={{ scale: 1.05, rotate: index % 2 === 0 ? 2 : -2 }}
                    className="relative group cursor-pointer"
                  >
                    <div className="absolute -inset-4 bg-white/5 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="relative w-full max-w-lg mx-auto rounded-[2rem] border border-white/10 shadow-2xl grayscale group-hover:grayscale-0 transition-all duration-700" 
                    />
                    <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-8">
                      <span className="text-white font-black text-2xl uppercase tracking-widest">{item.title}</span>
                    </div>
                  </motion.div>
                </div>

                <div className={`w-full lg:w-1/2 text-center ${index % 2 === 0 ? 'lg:text-left' : 'lg:text-right'} relative`}>
                  <motion.span 
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 0.03, scale: 1.5 }}
                    className="text-[15rem] font-black text-white select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 whitespace-nowrap"
                  >
                    {item.year}
                  </motion.span>
                  
                  <div className="relative z-10 space-y-6">
                    <motion.span 
                      whileHover={{ scale: 1.1, color: '#ffffff' }}
                      className="text-6xl md:text-8xl font-black text-white/20 block mb-4 transition-colors cursor-default"
                    >
                      {item.year}
                    </motion.span>
                    <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight">{item.title}</h2>
                    <p className="text-xl md:text-2xl text-gray-400 max-w-lg mx-auto lg:mx-0 leading-relaxed font-light">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default History;